import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig, GridRow } from '@inavista/components/grid';

// Service Order popup → Calibration tab on @inavista/components. Same columns, labels and widths as the Syncfusion grid
// it replaces. Rows are the item group's calibration types (calibration-tab/types) merged with the order's
// calibrations, held by the page (LocalRowsGridApi): the grid sorts, filters and searches them in the browser.

export const CALIBRATION_TAB_PERSISTENCE_KEY = 'rims.service.calibration-tab';
export const CALIBRATION_TAB_URL = 'page://calibration-tab';

const GREEN = '#5cb85c';
const INACTIVE = '#cccccc';
const EYE_ICON = '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />';

// The old hidden ID column (pageType) is the grid's key and was never in the column chooser: it is not a column here.
export const CALIBRATION_TAB_COLUMNS: [string, string][] = [
  ['calibrationType', 'nvarchar'],
  ['createdDate', 'datetime'],
];

export interface CalibrationTabGridContext {
  /** The Calibration Type cell: a link while the order is In Progress, plain text otherwise (as before). */
  calibrationTypeCell: TemplateRef<unknown>;
  /** The old AllGrid_Toolbar_Excel_Export role claim. */
  canExport: boolean;
}

// View certificate: green when the type has a certificate, grey and inert otherwise (the old rowDataBound rule).
export function buildCalibrationTabActionIcons(): ActionIconDef[] {
  return [{
    key: 'view', label: 'View certificate', icon: EYE_ICON,
    colorFn: (row: GridRow) => (row['hasCertificate'] ? GREEN : INACTIVE),
    disabledFn: (row: GridRow) => !row['hasCertificate'],
    labelFn: (row: GridRow) => (row['hasCertificate']
      ? 'View certificate'
      : 'No certificate yet. Open the calibration type to fill it in and create one.'),
  }];
}

export function buildCalibrationTabGridConfig(ctx: CalibrationTabGridContext): GridConfig {
  return {
    apiBaseUrl: CALIBRATION_TAB_URL,
    clientSide: true,
    keyColumn: 'pageType',
    rowHeight: 30,
    defaultColumnOrder: CALIBRATION_TAB_COLUMNS.map(([c]) => c),
    columnLabels: { calibrationType: 'Calibration Type', createdDate: 'Created Date' },
    columnAlign: {
      calibrationType: { header: { horizontal: 'left' } },
      createdDate: { header: { horizontal: 'left' } },
    },
    // Created Date and Actions were 150 wide; Calibration Type took the rest.
    columnWidths: { calibrationType: 400, createdDate: 150 },
    columnSizing: { createdDate: { noStretch: true } },
    actionsColumnMinWidth: 150,
    columnTypes: { createdDate: 'datetime' },
    // The old 'd MMM, y  hh:mm'.
    columnDateFormats: { createdDate: 'd MMM, yyyy  hh:mm' },
    cellTemplates: { calibrationType: ctx.calibrationTypeCell },
    // The old toolbar: Columns, Search, Excel export (with the role claim), Clear filters. Reset layout is kept: it
    // undoes the saved layout.
    enableExport: ctx.canExport,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    emptyStateMessage: 'No calibration types for this order\'s item group.',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: CALIBRATION_TAB_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
